/**
 * App changelog, shown by the version pill (components/VersionPill.jsx).
 *
 * Keep this in sync with CHANGELOG.md at the repo root (that file is the
 * git-history-facing record; this one is the in-app-facing summary — same
 * entries, written for end users rather than commit messages). Newest first.
 *
 * When you ship a user-visible change:
 *   1. Bump the "version" in web/package.json (semver: patch for fixes,
 *      minor for new features, major for breaking changes).
 *   2. Add an entry here with that same version.
 *   3. Add the matching entry to CHANGELOG.md.
 */
export const CHANGELOG = [
  {
    version: '1.112.0',
    date: '2026-10-30',
    highlights: [
      'Admins have a new Orbit feed page. When it is turned on, each completed matter is shared with Orbit, the firm’s relationship platform: the client, contact, practice group and attorneys, and the numbers once assigned. Never the matter name, parties, conflicts, fees, or walled matters.',
    ],
  },
  {
    version: '1.111.0',
    date: '2026-10-29',
    highlights: ['The application for employment now shows the firm’s logo at the top.'],
  },
  {
    version: '1.110.2',
    date: '2026-10-28',
    highlights: [
      'Wording you hide in the application editor, such as the self-identification note, is now hidden on the form applicants fill in too, not just in the preview.',
    ],
  },
  {
    version: '1.110.1',
    date: '2026-10-28',
    highlights: ['Fixed: in the application’s tables, dropdowns are now the same height as the boxes beside them.'],
  },
  {
    version: '1.110.0',
    date: '2026-10-28',
    highlights: [
      'Short questions now share a line, such as first, middle and last name, or city, state and ZIP. Any question can be set to full, half or third width.',
      'Each employer takes five lines instead of nine, and every table opens with one blank entry ready to fill in.',
      'Tick boxes now sit beside their statements.',
      'The privacy notice’s acknowledgement and signature are required again.',
      'The race question now shows even when the Hispanic or Latino question is left blank.',
    ],
  },
  {
    version: '1.109.1',
    date: '2026-10-28',
    highlights: [
      'Candidate conflict check: Office is now required when inviting a candidate. A candidate sent a form without one could not submit it; those forms can now be submitted.',
      'Candidate form: clicking the certification wording now ticks the box, a form can no longer be reopened or sent to HR twice by saving or uploading while it is being submitted, and the page now says when changes could not be saved instead of failing silently.',
    ],
  },
  {
    version: '1.109.0',
    date: '2026-10-28',
    highlights: [
      'The application form is wider, and a school now fits on one line.',
      'Any table on the application can be set to show 1 to 6 boxes per line.',
      'Fixed: choosing “Show when…” no longer runs off the screen when a question is long.',
    ],
  },
  {
    version: '1.108.0',
    date: '2026-10-27',
    highlights: [
      'Every button and message on the application can be reworded on the form editor, including what applicants are told when something is missing or a link has stopped working.',
      'Both application emails can be reworded line by line, and lines can be hidden.',
      'Any box in a table can be made required — each reference’s name, address and contact, for example.',
      'The race choices are listed out so their wording wraps, and any dropdown can be shown that way.',
      'Fixed: the email to HR no longer says a privacy notice or self-identification form is attached when it is not.',
    ],
  },
  {
    version: '1.107.0',
    date: '2026-10-26',
    highlights: [
      'Self-identification now follows the firm’s updated EEO form (2/2025): Non-Binary added, the new Hispanic or Latino wording, and seven race categories including Middle Eastern or North African.',
      'Everything on the application can now be edited, including the privacy notice’s text and date, whether a self-identification question is required, and hiding or moving the privacy notice and self-identification sections.',
    ],
  },
  {
    version: '1.106.2',
    date: '2026-10-23',
    highlights: [
      'The New Matter Memo now shows when the matter was submitted, in Pacific time. Before, it read "Submitted —".',
      'A client picked with "Select Current Client" now prints on the memo as its number and name, instead of "[object Object]".',
      'The Date on a new matter is today. In the evening it used to show tomorrow.',
      'If your last changes could not be saved, the form no longer submits an older copy. It tells you, and you can try again.',
    ],
  },
  {
    version: '1.106.1',
    date: '2026-10-20',
    highlights: [
      'Download matter record PDF now gives the copy with the client and matter numbers once they are assigned. Before, it kept giving the earlier copy without them.',
    ],
  },
  {
    version: '1.106.0',
    date: '2026-10-20',
    highlights: [
      'Application form editor: Hide buttons for the box at the top, section introductions, the section notes ("Answering this part is entirely your choice…"), and the line and download button shown after submitting.',
      'The privacy notice and self-identification PDFs now show the applicant’s name, position, email and date submitted at the top.',
    ],
  },
  {
    version: '1.105.0',
    date: '2026-10-20',
    highlights: [
      'Application form editor: now you can add and remove dropdown choices and table columns, change when a question is shown, move questions between sections, and add, hide or reorder sections.',
      'New question kinds on the application: tick box and table. The page title, the thank-you message and the Yes/No answers can be reworded too.',
      'The privacy notice section has its own note, separate from the self-identification one.',
      'Fixed: a page wording box could show empty in the editor while applicants saw the original words.',
    ],
  },
  {
    version: '1.104.0',
    date: '2026-10-19',
    highlights: [
      'Application form editor, rebuilt: click any words on the form (headers, questions, help text, statements, dropdown choices, table columns, the button) and type. Hover a question for required/optional, moving it, or hiding it.',
      'Help text now shows on the application. It was never displayed before.',
    ],
  },
  {
    version: '1.103.1',
    date: '2026-10-19',
    highlights: [
      'Application form editor: the statements on the form (equal opportunity, self-identification) can now be edited. Click one in Edit mode to change its wording.',
    ],
  },
  {
    version: '1.103.0',
    date: '2026-10-16',
    highlights: [
      'The application for employment can be edited on the form itself: open “View and edit the form” on Applications, switch to Edit, and change any part of it in place: questions, help text, sections, order, what is required, added questions, and the wording at the top and on the button. Nothing changes for applicants until you save.',
    ],
  },
  {
    version: '1.102.0',
    date: '2026-10-10',
    highlights: [
      'Candidate conflict form: candidates who upload their matter list now download a template with the same columns as the form’s Matters table. Only a completed copy is accepted; anything else is refused straight away with a message saying what to fix.',
    ],
  },
  {
    version: '1.101.1',
    date: '2026-10-10',
    highlights: [
      'The conflict check request works better on phones: tapping a field no longer zooms the page on iPhone, the buttons are easier to tap, and the header fits.',
    ],
  },
  {
    version: '1.101.0',
    date: '2026-10-01',
    highlights: [
      'Conflict check request: a new, optional “Email subject line” box. Whatever you type (the plaintiff’s last name, say) is added to the end of the email’s subject, so your request is easy to find. The line under the box shows the subject as it will be sent.',
    ],
  },
  {
    version: '1.100.0',
    date: '2026-10-01',
    highlights: [
      'Behind-the-scenes groundwork on the conflict check request.',
    ],
  },
  {
    version: '1.99.2',
    date: '2026-09-29',
    highlights: [
      'Removed the “Become superadmin” button from Admin → Roster. Superadmin access is now granted only by an existing superadmin.',
    ],
  },
  {
    version: '1.99.1',
    date: '2026-09-29',
    highlights: [
      'Conflict check request: a name typed into “Copy others” but not picked from the list is no longer dropped. The form now stops and asks you to pick the person, or clear the box, before it sends.',
      'Pressing Enter in “Copy others” adds a person only when your typing matches exactly one; otherwise it asks you to pick from the list.',
      'Admin → Mail Queue now shows who each message was copied to.',
    ],
  },
  {
    version: '1.99.0',
    date: '2026-09-28',
    highlights: [
      'Admin → Conflicts now shows how much the conflict check request page is used: requests and the names or entities on them, over the last 7 days, 30 days, this year and all time, with a twelve-month history.',
    ],
  },
  {
    version: '1.98.0',
    date: '2026-09-25',
    highlights: [
      'Opening nmm.aalrr.com signs you in automatically. No button, no switch; if it ever misbehaves in one browser, https://nmm.aalrr.com/?sameorigin=0 turns it off there.',
      'Opening the app takes you straight to the New matter form. My matters is one click away on the left rail.',
      'The “Copy others” box on the conflict check request finds people again; it had been blocked at the server, not the page.',
    ],
  },
  {
    version: '1.97.1',
    date: '2026-09-25',
    highlights: [
      'Fixed a blank page on load, present since 1.96.0. If the app showed nothing at all, or automatic sign-in appeared to do nothing, this was it.',
    ],
  },
  {
    version: '1.97.0',
    date: '2026-09-25',
    highlights: [
      'The “Copy others” box on the conflict check request now finds everyone at the firm, not only the people with a role in this app. Start typing a name or address; the list itself is never sent to your browser, only the matches.',
      'Admin → Directories has a People tab: how many people the firm index holds, when it was last rebuilt, and a button to rebuild it from Entra now. It rebuilds itself every night.',
    ],
  },
  {
    version: '1.96.0',
    date: '2026-09-25',
    highlights: [
      'Signing in automatically is available again, switched on per browser while it is proved out. The “Sign in with AALRR” button keeps working either way.',
    ],
  },
  {
    version: '1.95.0',
    date: '2026-09-25',
    highlights: [
      'Removed the “No admin yet? Claim access” button and the endpoint behind it. It was a first-run shortcut that had long outlived its purpose, and access is now granted only by an existing administrator.',
      'Sign-in is back to the “Sign in with AALRR” button. Signing in automatically was tried and did not work reliably, so it has been removed rather than left half-working.',
    ],
  },
  {
    version: '1.93.0',
    date: '2026-09-25',
    highlights: [
      'The conflict check request form has a “Copy others” box: start typing a name or address and the people you pick are shown on the form. They are copied on the request, and remembered for your next one.',
      'The conflict check page now has the same banner and version pill as the other pages, and a Sign out button.',
    ],
  },
  {
    version: '1.92.0',
    date: '2026-09-25',
    highlights: [
      'You are now signed in automatically when you open the app, instead of having to press “Sign in with AALRR”. The sign-in screen only appears if that does not work, and it explains why.',
      'That screen now names the tool you were opening — the conflicts page, candidate conflict checks, and so on — rather than always saying New Matter Intake.',
    ],
  },
  {
    version: '1.91.0',
    date: '2026-09-23',
    highlights: [
      'The signed CCPA privacy notice now arrives as its own PDF for each applicant, rather than as pages inside the application, so it can be kept separately. Applicants still sign it on the same form.',
    ],
  },
  {
    version: '1.90.2',
    date: '2026-09-23',
    highlights: [
      'Fixed: the “start from a template” list on My matters had been empty since templates were made private. Your templates are there again.',
      'Fixed: the list of employment applications failed to load.',
    ],
  },
  {
    version: '1.90.1',
    date: '2026-09-23',
    highlights: [
      'Fixed: editing a section heading or its paragraph on the candidate form and pressing Save made the change disappear.',
    ],
  },
  {
    version: '1.90.0',
    date: '2026-09-23',
    highlights: [
      'Admin → Roster can now look people up in the firm directory: search by name or email and start their record from the result, instead of typing their details in. Their roles and practice groups are still yours to choose.',
    ],
  },
  {
    version: '1.89.0',
    date: '2026-09-23',
    highlights: [
      'There is a new "Form Editor" role. It lets somebody edit the questions and wording on the candidate conflict form and the employment application without giving them administrator access — they cannot change who receives completed forms, which mailbox sends them, or anything else.',
    ],
  },
  {
    version: '1.88.0',
    date: '2026-09-23',
    highlights: [
      'On the candidate conflict form you can now edit each section’s heading and the paragraph underneath it, not just the questions.',
      'That wording can carry formatting too: **two asterisks** for bold, and Enter for a new line. It works in headings, section paragraphs and question help text.',
    ],
  },
  {
    version: '1.87.1',
    date: '2026-09-23',
    highlights: [
      'A failed email in Admin → Mail queue now carries a plain-English explanation under the technical error, so it is clearer whether the problem is a permission, a wrong address, or something that cannot work at all.',
    ],
  },
  {
    version: '1.87.0',
    date: '2026-09-23',
    highlights: [
      'The wording on the conflicts request page can now carry formatting: wrap a word in **two asterisks** to make it bold, and press Enter to start a new line.',
    ],
  },
  {
    version: '1.86.0',
    date: '2026-09-23',
    highlights: [
      'The conflicts request email can now be sent under a different name, or from a different mailbox, set in Admin → Conflicts. Changing the mailbox needs IT to allow it first; changing only the name does not.',
    ],
  },
  {
    version: '1.85.0',
    date: '2026-09-23',
    highlights: [
      'conflicts.aalrr.com now opens the conflict check request page directly, and candidateconflicts.aalrr.com opens candidate conflict checks. Links to a specific page on those addresses still go where they point.',
    ],
  },
  {
    version: '1.84.2',
    date: '2026-09-22',
    highlights: [
      'Fixed: a keyword alert written with quotation marks around the phrase never matched anything. Quotes are now ignored, and the alert settings name the questions being searched rather than just counting them.',
    ],
  },
  {
    version: '1.84.1',
    date: '2026-09-22',
    highlights: [
      'Fixed: pressing the submit button at the bottom of the form threw an error instead of opening the confirmation dialog.',
    ],
  },
  {
    version: '1.84.0',
    date: '2026-09-22',
    highlights: [
      'Administrators can now set up keyword alerts under Admin → Alerts: when a submitted matter contains a word or phrase you are watching for, named people are emailed about it, with the sentence it appeared in.',
      'You choose which questions to watch and who hears about it — a role such as the General Counsel, a named person, or any email address. Alerts never gate or delay a matter; they are notifications only.',
    ],
  },
  {
    version: '1.83.0',
    date: '2026-09-22',
    highlights: [
      'In the practice run, click any question and the guide explains that question — what it means, whether it is required, and any rule it has to obey. Click the background to go back to the tour.',
      'The practice run now also covers My matters and Templates, highlighting each one in the rail.',
      'The guide starts in the middle of the screen, can be dragged wherever you want it, and has a bigger size if you prefer.',
    ],
  },
  {
    version: '1.82.0',
    date: '2026-09-22',
    highlights: [
      'The practice run now happens on the real form rather than in a window on top of it. Real pages, real fields, real dropdowns — with a small guide beside them you can collapse or close. Nothing you type in it is saved.',
      'Help now explains Templates: what they are for, and that only you can see your own.',
    ],
  },
  {
    version: '1.81.0',
    date: '2026-09-22',
    highlights: [
      'Templates are now private to you. They were visible to everyone signed in, and a template carries a real client’s details — so now only the person who saved one can see it.',
      'Buttons that silently did nothing (Delete on Templates, and intermittently others) should work again. The project had run out of its cloud CPU allowance, so calls were being refused before they ever reached the app.',
      'You can type a space in the Help page editor again.',
    ],
  },
  {
    version: '1.80.0',
    date: '2026-09-22',
    highlights: [
      'The walkthrough is now a practice run at the real form rather than a description of it. You click through your own pages, with your own fields — type into them, open the dropdowns, answer a question and watch the next one appear. Nothing you do in it is saved.',
      'A page list down the side shows where you are and lets you jump between pages.',
      'Help and the walkthrough no longer talk about approvals. Finishing a matter produces the PDF and routes it nowhere, and both now say so.',
      'Administrators can edit the Help page in the app, under Admin → Help page.',
    ],
  },
  {
    version: '1.79.0',
    date: '2026-09-22',
    highlights: [
      'When you submit a conflict check from the Conflicts page you now get a copy of it yourself, so there is a record of what you asked to have searched.',
      'The request also goes out as a spreadsheet: one sheet of the names to search — in whatever columns your conflicts page is set up with — and one sheet with the description and who sent it. It has empty columns for who searched, when, and what came back.',
    ],
  },
  {
    version: '1.78.0',
    date: '2026-09-22',
    highlights: [
      'There is now a guided walkthrough. It plays once, the first time you use the app, and follows one ordinary matter from the first question through to the approvals. Nothing in it is saved or submitted.',
      'A new Help page in the left rail explains what every page of the form is for, what happens after you submit, and the questions people actually ask — including what to do when a matter has not moved, and how to fix a mistake after submitting.',
      'Both read your firm’s form as it stands today rather than a fixed list, so they walk the pages you actually have, in your order, under your names — and follow along when an administrator renames, hides or adds one.',
      'You can replay the walkthrough any time from Help. Skipping it counts as having seen it, so it will not keep asking.',
    ],
  },
  {
    version: '1.77.1',
    date: '2026-09-17',
    highlights: [
      'The candidate form now asks for the employer and the dates of employment as two questions rather than one, so the conflicts spreadsheet can carry the firm name on its own — the most important thing on the list to search. Forms already submitted still read correctly.',
    ],
  },
  {
    version: '1.77.0',
    date: '2026-09-17',
    highlights: [
      'Every completed candidate conflict form now arrives with a spreadsheet as well as the PDF: one row per name to search, deduplicated, with the matter or client that identifies it. Also downloadable from /ccc.',
      'It does not try to split “adverse parties” into separate names — those are carried across word for word and flagged to read, because splitting on commas invents parties that were never named.',
      'If the candidate attached their matter list rather than typing it, the first row of the spreadsheet says so and names the file, so a short list is never mistaken for a complete one.',
    ],
  },
  {
    version: '1.76.0',
    date: '2026-09-17',
    highlights: [
      'The firm’s Application for Employment is now an online form. HR generates a link at /apply and sends it themselves — usually in the same email as the conflict-check link — and the applicant fills it in without signing in, with their answers saving as they go.',
      'It includes the CCPA privacy notice in full, with a signature, and the application and at-will acknowledgements. Each needs a tick and a typed name.',
      'The voluntary gender, race and ethnicity form is sent to HR as a separate attachment, labelled as one to keep apart from the personnel file. Leave it blank and no such document is created at all.',
      'The questions can be edited from /apply without a deploy, the same way the conflict form’s can — except the voluntary ones, which cannot be made required.',
    ],
  },
  {
    version: '1.75.0',
    date: '2026-09-15',
    highlights: [
      'A practice group can now have its own approval sequence — “General Counsel, then the Practice Group Leader, then the Responsible Partner”, in whatever order that group works in. Set it up in Admin → Approval Flow → Practice group flows.',
      'Where a group has its own flow, that list is the whole flow: the answer-based rules above are not applied as well, so anyone who must see the matter needs to be on the list. Groups without one are unchanged.',
      'Fixed: the “also copy on this email” list added in the last release was never actually reaching the email.',
    ],
  },
  {
    version: '1.74.0',
    date: '2026-09-15',
    highlights: [
      'A step can now go to the Responsible Partner named on the matter — so two matters in the same practice group can route to different people.',
      'Every step can copy other people: Managing Partner, General Counsel, Practice Group Leader, Responsible Partner, anyone on the roster, or a group email address. They receive the email only — being copied never lets someone approve.',
      'Fixed: choosing “Team Leader” as an approver produced a step that failed the whole submission. It resolves properly now.',
      'Fixed: an unrecognised approver setting used to stop anyone submitting anything. It now blocks that one step and routes it for reassignment, the same as any step whose approver cannot be found.',
    ],
  },
  {
    version: '1.73.0',
    date: '2026-09-14',
    highlights: [
      'A candidate whose five-year matter list is too long for the table can now attach it as a file instead. The wording tells them it has to contain everything the table asks for, or it cannot be run through a conflicts check.',
      'Whatever they attach is emailed to HR alongside the PDF, and named in the PDF itself.',
      'Up to five files, 3 MB each and 3 MB in total — spreadsheets, documents, PDFs and images. Attachments can be removed until the form is submitted, and not after.',
    ],
  },
  {
    version: '1.72.1',
    date: '2026-09-11',
    highlights: [
      'The candidate conflict pages now carry a firm banner — the same navy band as the emails they arrive from, so a candidate clicking a link lands somewhere that looks like the firm rather than on an untitled form.',
      'The version pill sits on /ccc but not on the candidate’s page.',
    ],
  },
  {
    version: '1.72.0',
    date: '2026-09-11',
    highlights: [
      'The candidate conflict form now asks the questions the General Counsel added: bar admissions in every jurisdiction, proceedings the candidate has been a party to, gaps in malpractice cover, and an undertaking to keep HR informed until their start date.',
      'It ends with the firm’s certification and a typed signature. The form cannot be submitted until the box is ticked — unlike every other question on it, “no” is not an available answer.',
      '“Is the matter open or closed?” is now a dropdown, and “Any AALRR involvement” is Yes/No with a follow-up asking who — per matter, not per form.',
      'The PDF sent to HR lays the longer tables out as labelled entries, which is readable where seven columns across a page is not.',
    ],
  },
  {
    version: '1.71.4',
    date: '2026-09-10',
    highlights: [
      'Fixed: Open on a candidate form failed with “snap is not defined”. Nothing was wrong with the form — the page could not fetch it.',
      'The link at the foot of the completed-form email is no longer called “Open it in the intake app”, which is a different tool. It now says “Open the submitted form”, and an administrator can rename it in Settings.',
    ],
  },
  {
    version: '1.71.1',
    date: '2026-09-10',
    highlights: [
      'Inviting a candidate now picks the office and the practice group leader from dropdowns of the firm’s own records, instead of typing them.',
    ],
  },
  {
    version: '1.71.0',
    date: '2026-09-10',
    highlights: [
      'A candidate’s link can now be switched off, or replaced with a fresh one — issuing a new link switches the old one off. Their answers are kept either way.',
      'Old forms can be deleted by an administrator, confirmed by typing the candidate’s name. It destroys the answers and the stored PDF; the copy already emailed to HR is unaffected.',
      'You now see the forms you sent rather than everybody’s. Administrators see them all, so a link can be revoked on behalf of someone away.',
      'Fixed: any signed-in user could open any candidate’s form if they had its id — the list was scoped but the fetch was not.',
    ],
  },
  {
    version: '1.70.0',
    date: '2026-09-10',
    highlights: [
      'The candidate form’s questions are now editable on /ccc — reword them, change what is required, reorder them, relabel a table’s columns, hide one, or add your own. It takes effect immediately on the form, the PDF and the validation.',
      'A question’s type cannot be changed and a built-in one cannot be deleted, only hidden: answers are already stored against them, and renaming or removing a question would orphan the answers given to it.',
      'The invitation’s subject line is editable too. Write {name} or {position} to have them filled in.',
    ],
  },
  {
    version: '1.69.7',
    date: '2026-09-10',
    highlights: [
      'Fixed: candidate emails still came from the intake mailbox despite the settings. The values were saved but never handed to the mail transport.',
      'Fixed: those emails rendered badly in Outlook — stretched header, and the button appearing as underlined text. The layout is now built the way Outlook requires.',
      'Fixed: the six-column “legal work over the past five years” table was cramped and wrapped awkwardly. Wide tables now put one field per row.',
    ],
  },
  {
    version: '1.69.6',
    date: '2026-09-10',
    highlights: [
      'Candidate emails now arrive from “Candidate Conflicts” rather than the name of whichever mailbox sends them. Change it under From name in Settings on /ccc.',
    ],
  },
  {
    version: '1.69.5',
    date: '2026-09-10',
    highlights: [
      'The candidate form and /ccc now have their own browser tab title and icon, rather than showing “New Matter Intake”. A candidate’s tab said “New Matte…”, which reads like something forwarded to them by mistake.',
    ],
  },
  {
    version: '1.69.4',
    date: '2026-09-10',
    highlights: [
      'Fixed: the candidate form showed “[object Object]” in the “Other names” box as soon as anyone typed in it.',
      'Fixed: the details filled in from the invitation looked like an empty form. They now read as label and value rather than greyed-out input boxes.',
    ],
  },
  {
    version: '1.69.3',
    date: '2026-09-10',
    highlights: [
      'Candidate form settings are now editable on the page itself — who receives a completed form, who is copied, which mailbox sends it, the domain candidates see, and the invitation wording. Previously these lived only in the database.',
    ],
  },
  {
    version: '1.69.2',
    date: '2026-09-10',
    highlights: [
      'Fixed: generating a candidate link failed with “MAGIC_LINK_SECRET is not configured”. The secret was in place; the function that signs the link had not been told it could use it.',
    ],
  },
  {
    version: '1.69.1',
    date: '2026-09-10',
    highlights: [
      'Candidate conflict checks moved to their own page at /ccc, separate from the New Client/Matter form and its navigation — a candidate check is not a step in opening a matter. The old links redirect.',
    ],
  },
  {
    version: '1.69.0',
    date: '2026-09-10',
    highlights: [
      'New: the Candidate Conflict Check and Professional Disclosure form can now be filled in by the candidate themselves. Go to /ccc to create a private link — no sign-in for them, since a candidate has no firm account.',
      'Generate the link and send it from your own email, or have the app send the invitation for you. Either way you keep a copy of the link.',
      'The domain candidates see can be a firm domain rather than the app’s own — add it in Firebase Hosting and set publicBaseUrl on settings/candidateForm.',
      'Their answers save as they type, so a four-page form can be finished over more than one sitting.',
      'When they submit, the completed form is emailed to HR as a PDF and the candidate can download their own copy. Set who receives it — and which mailbox it is sent from — on settings/candidateForm.',
      'Name, position, office and practice group leader are pre-filled from the invitation and cannot be changed by the candidate.',
    ],
  },
  {
    version: '1.68.2',
    date: '2026-09-09',
    highlights: [
      'Each auto-fill rule now says what it does in a sentence — “Select Current Client → its City fills City” — so you can find the one you came to change without reading four dropdowns in every card.',
      'Fixed: saving auto-fill rules could silently undo a change made elsewhere. A page left open kept a stale copy and wrote it back over newer rules. It now picks up the newer version, or warns you before you overwrite it.',
    ],
  },
  {
    version: '1.68.1',
    date: '2026-09-09',
    highlights: [
      'Fixed: an import that skipped every row would not say why. It now names the column that is missing from the file and lists what the file actually calls its columns — the cause was a mapping saying CLIENT_NAME where the export says Client_Name.',
      'Fixed: a column’s header could not be edited, so a mapping pointing at a renamed column could not be repaired at all.',
      'The feeds page now warns, in red, when the client number or name column is not in the export that arrived — the two whose absence skips every single row.',
    ],
  },
  {
    version: '1.68.0',
    date: '2026-09-09',
    highlights: [
      'Filling the Client Information boxes from an existing client is now set up in Auto-fill rules, alongside every other piece of cross-field logic — one “Look up a related record” rule per box, pointed at the client picker.',
      'What a rule can copy out of a client comes from that feed’s own columns, under the same names they were given on the feeds page.',
      'The value is the copy saved when the client was chosen, so a matter keeps what it was filled in with — a later export cannot rewrite a matter that is already done.',
      'The feeds page’s “Fills in this form field” column still works but is now the older way; new ones belong in Auto-fill rules, so this is configured in one place instead of two.',
    ],
  },
  {
    version: '1.67.0',
    date: '2026-09-09',
    highlights: [
      'Picking an existing client now fills in the Client Information boxes — name, street address, city, state, ZIP, Send Invoice To and Invoice Email Address — and clears them again if you clear the selection. Anything you correct stays corrected.',
      'Admin → Directories → Feeds can now clean up an export column: keep the first of several email addresses in one cell, and sort out the two address lines so the ATTN line stops landing in Street Address.',
      'Fixed: the new client export would have lost 611 clients, silently. A contact name containing a comma made the row one field too long, and rows the importer could not put back together are refused — so those clients would simply have been missing from the picker. Which column the commas are in is now a setting.',
      'Fixed: filling in a client could leave a Yes/No question showing nothing selected while still counting as answered — so matters went to approval with a question that looked blank. Yes/No and dropdown boxes now get a real answer, or are left blank so submission asks about them.',
      'Fixed: the word NULL was being stored, and filled in, as though somebody had typed it.',
    ],
  },
  {
    version: '1.66.0',
    date: '2026-09-09',
    highlights: [
      'Admin → Workflow can now hide the “Shared with” card. It hides the control only — anyone a matter is already shared with keeps their access.',
      'The submit button’s text is editable in the same place. Leave it empty for the default.',
    ],
  },
  {
    version: '1.65.2',
    date: '2026-08-29',
    highlights: [
      'Fixed: the box for explaining a blank field disappeared mid-sentence, and the half-typed reason was what got submitted. Rows now stay on screen and are ticked once explained.',
      'Fixed: the matter record printed the raw field name for an explained field instead of its label.',
    ],
  },
  {
    version: '1.65.1',
    date: '2026-08-29',
    highlights: [
      'Fixed: a roster re-import would have demoted the seventeen people whose row has no title — including the General Counsel — to staff. A blank title now leaves an existing person unchanged.',
    ],
  },
  {
    version: '1.65.0',
    date: '2026-08-29',
    highlights: [
      'Fixed: the roster import gave everyone the attorney role regardless of their title. It now comes from the title, and deliberately granted roles survive a re-import.',
      'Fixed: signing in created a second copy of the person in the roster. The original is retired instead of left behind, and a re-import updates the existing row.',
    ],
  },
  {
    version: '1.64.3',
    date: '2026-08-29',
    highlights: [
      'Fixed: submitting a matter with a soft-required field left blank failed with a database error. Every submission that waived a field was affected.',
    ],
  },
  {
    version: '1.64.2',
    date: '2026-08-29',
    highlights: [
      'A percentage field now holds its configured range — a value outside it is brought back inside when you leave the field, instead of only being caught at submission.',
    ],
  },
  {
    version: '1.64.1',
    date: '2026-08-29',
    highlights: [
      'A field row in Field builder is four columns and one line high — help text, prompts, width, new line and the two flags all moved behind the Edit button.',
      'The expanded panel is grouped: Placement, Help text and prompts, Flags, then the condition editors.',
      'Every cell is top-aligned and the rows are all the same height, and the table no longer stretches to leave a gap.',
    ],
  },
  {
    version: '1.64.0',
    date: '2026-08-29',
    highlights: [
      'Field rules and Field builder are one page now — the per-field settings were duplicated, and they live on the field in the Layout tab.',
      'The firm-wide half (label markers, and the rules for explaining a blank soft-required field) is Field builder → Markers & explanations.',
      'Nothing configured was lost, and the old Field rules link redirects.',
    ],
  },
  {
    version: '1.63.1',
    date: '2026-08-29',
    highlights: [
      'Field Builder no longer has a wide empty band beside every field label — the columns are sized to what is in them.',
      'Requirement and Move to page are wide enough to read their selected option, and the two checkbox columns are no longer squashed together.',
      'A field’s editors now open in their own full-width row, so they have room instead of being squeezed into one column.',
    ],
  },
  {
    version: '1.63.0',
    date: '2026-08-29',
    highlights: [
      'A people picker now closes when you click away from it, or press Escape — no need to find the Close link.',
      'Field Builder is quieter: each field’s editors sit behind one Edit button, and a row shows small labels for whatever is configured on it.',
    ],
  },
  {
    version: '1.62.0',
    date: '2026-08-29',
    highlights: [
      'Auto-fill rules can now hold their field against being changed — tick “Don’t allow changes” and the field is greyed out with the rule’s value in it.',
      'A set-a-default rule holds the field only while its condition matches; when it stops matching the field becomes editable again and keeps its value.',
    ],
  },
  {
    version: '1.61.0',
    date: '2026-08-29',
    highlights: [
      'Field Builder: a field can be greyed out always, or only when a condition matches — it updates live as the form is filled in.',
      'Field Builder: a starting value can also carry a condition, so it only applies in the cases you choose.',
      'Both sit next to conditional visibility, so when a field is hidden and when it is read-only are answered in one place.',
    ],
  },
  {
    version: '1.60.0',
    date: '2026-08-29',
    highlights: [
      'Admin → Field Rules: a field can now have a starting value, filled in when a matter is created or when the field first appears. Clearing it leaves it cleared.',
      'Admin → Field Rules: a field can now be shown but not editable — it still appears and still prints, greyed out.',
      'The origination maximum per attorney now applies to a single attorney too, so origination credit has to be shared — one name can no longer hold 100% alone.',
    ],
  },
  {
    version: '1.59.5',
    date: '2026-08-29',
    highlights: [
      'The browser-tab icon is now the same mark as the one at the top of the left rail.',
    ],
  },
  {
    version: '1.59.4',
    date: '2026-08-29',
    highlights: [
      'The top bar is shorter (58px), giving back some vertical space on every page.',
    ],
  },
  {
    version: '1.59.3',
    date: '2026-08-29',
    highlights: [
      'The intake section list now uses plain circles rather than numbered badges — filled once a section is complete, ringed green on the one you are on.',
    ],
  },
  {
    version: '1.59.2',
    date: '2026-08-29',
    highlights: [
      'New releases now show up as soon as you reload. Previously a release could take up to an hour to appear unless you hard-refreshed.',
    ],
  },
  {
    version: '1.59.1',
    date: '2026-08-29',
    highlights: [
      'The app mark at the top of the left rail no longer sits in a blue box — the green page is larger and sits directly on the navy.',
    ],
  },
  {
    version: '1.59.0',
    date: '2026-08-28',
    highlights: [
      'Every colour in the app now comes from the firm’s brand palette — the navy, medium blue, greens and orange from the colour deck — in both light and dark mode.',
      'Dark mode is navy rather than black, so the rail, the page and the cards read as one product.',
      'Yes/No answers are now buttons that fill green when chosen, currency fields carry the “$” inside the box, and warnings take an orange rule and an icon.',
      '“New matter” is the first item in the left rail.',
      'Fixed: primary buttons had no hover state at all, and text boxes were invisible in dark mode.',
    ],
  },
  {
    version: '1.52.1',
    date: '2026-08-21',
    highlights: [
      "Data feeds is now a sub-tab of Directories rather than its own admin tab — the records and the feed that fills them live in one place. Old links to Data feeds still work.",
    ],
  },
  {
    version: '1.52.0',
    date: '2026-08-21',
    highlights: [
      'New Admin → Directories page: browse everything a feed imported, with a total of active and inactive records, a search box, and an All / Active only / Inactive only filter.',
      'Shows every column the feed keeps, and each record’s status exactly as the export spelled it, so an import can be checked rather than guessed at.',
    ],
  },
  {
    version: '1.51.1',
    date: '2026-08-21',
    highlights: [
      'Fixed adding a Client field in Field Builder loading a blank page.',
    ],
  },
  {
    version: '1.51.0',
    date: '2026-08-21',
    highlights: [
      "A feed that imports nothing now tells you why in plain words — \"exactly one column must be marked as the client number\" — instead of printing an internal error code.",
      'Mapping an export is now mostly confirmation: press Map these columns and each one arrives with a suggested role already chosen, read from its heading.',
      'The row count shown for a discovered export is now the real number of rows in the file.',
    ],
  },
  {
    version: '1.50.1',
    date: '2026-08-20',
    highlights: [
      'Fixed a security-rule error that stopped 1.50.0 from deploying at all, and a long-standing one that prevented every data-feed panel from showing when its feed last ran.',
    ],
  },
  {
    version: '1.50.0',
    date: '2026-08-20',
    highlights: [
      'You can add data feeds yourself now — Admin → Data feeds → Add a feed. Give it an id, a name and an email subject, map its columns, and it behaves exactly like the client feed. No developer needed.',
      'Load a CSV by hand to seed a directory or test a mapping: it runs the same import the nightly poll runs, and can be undone the same way, so you never have to email yourself a file to try something.',
      'Every column now has an explicit choice of what to do with it — including "Ignore this column", for exports where most columns are not wanted.',
      'A picker field can point at any directory you have added, so a new feed becomes usable on the form immediately.',
    ],
  },
  {
    version: '1.49.1',
    date: '2026-08-20',
    highlights: [
      'Fixed three silent data-loss bugs found by the first real client export: one stray quote mark was swallowing thousands of rows, 40% of clients were having their status, office and attorney shifted one column across by unquoted commas in the name, and the importer would have stored nothing at all while reporting success.',
      'The quote fix also protects the people/roster feed, which shares the same file reader.',
    ],
  },
  {
    version: '1.49.0',
    date: '2026-08-20',
    highlights: [
      'Test the client feed safely: Admin → Data feeds now shows how many clients are in the directory, with an "Undo the last import" button that removes exactly what one export wrote and lets you re-import a corrected version of the same file.',
      'Emptying the whole directory is possible but deliberately harder — it needs the phrase DELETE ALL CLIENTS typed out. Every removal is recorded in the audit log.',
    ],
  },
  {
    version: '1.48.1',
    date: '2026-08-20',
    highlights: [
      'Fixed the intake form loading to a blank page — a regression from the submit-banner change in 1.46.0 that affected every load.',
    ],
  },
  {
    version: '1.48.0',
    date: '2026-08-20',
    highlights: [
      'The client export can now carry an active/inactive column — mark it in Admin → Data feeds and inactive clients drop out of the client picker.',
      'Its values do not need to be agreed in advance: Active/Inactive, A/I, Y/N, true/false, 1/0 and Open/Closed are understood, and you can name any others — including reversing one, for an export where "Y" means inactive.',
      'Inactive clients can optionally still be offered, marked "(inactive)", for opening a new matter for a client the source system has closed.',
      'Anything blank or unrecognized counts as active rather than hiding a client, and each import reports how many values it could not read so a mis-mapped column is obvious.',
    ],
  },
  {
    version: '1.47.0',
    date: '2026-08-20',
    highlights: [
      'New Admin → Data feeds page: the app can now receive a client directory export by email the same way it receives the people roster, and you map the export’s columns yourself.',
      'The first run of a new feed records the export’s real column headings without importing anything, so the mapping can be finished from the actual file — one click turns those headings into rows to map.',
      'Each column can name the form field it should fill in when a client is chosen, or be imported and left searchable without filling anything in.',
      'New "Client (from the client directory)" field type for Field Builder — searches by client number and client name at once, so "new matter for an existing client" no longer means retyping the client.',
    ],
  },
  {
    version: '1.46.0',
    date: '2026-08-20',
    highlights: [
      'A reason for leaving a soft-required field blank now has to be a real explanation — "n/a", "none", "tbd" and the like are rejected, along with anything under a minimum length and word count you set in Admin → Field rules.',
      'Those reasons are now frozen onto the matter at submission and can be printed on the record PDF and shown to approvers in their approval email — both admin-selectable, and stamped per matter so changing the setting never alters a record already filed.',
      'The "please complete the highlighted fields" banner now clears itself as soon as the last problem is fixed, and names the pages to look at: "2 fields need attention in Litigation and Client Source & Risk Screening."',
      'People choosers no longer list the same person twice, and show every name in capitals to match the directory feed.',
    ],
  },
  {
    version: '1.45.0',
    date: '2026-08-20',
    highlights: [
      'The origination split must now total exactly 100% before you can submit — including a split with just one attorney, which used to be exempt and prefilled at 75%.',
      'A single originator can now hold the full 100% despite the 75%-per-attorney firm cap; the cap governs shared splits and re-applies as soon as a second attorney is added.',
      'A share over the firm cap is now flagged on the form instead of only being rejected at submit.',
      "The running origination total is shown even when there's only one attorney.",
    ],
  },
  {
    version: '1.44.0',
    date: '2026-08-19',
    highlights: [
      'The approval workflow can now be turned on for specific people while it stays off for everyone else — Admin → Workflow → Pilot — so it can be tested end to end on real matters.',
      'A matter now remembers whether it went through the workflow, so an approved record keeps its approval trail even if the workflow is switched off later.',
    ],
  },
  {
    version: '1.43.0',
    date: '2026-08-19',
    highlights: [
      'An attorney left at 0% in the Origination Split now prompts by name — "Bob Associate has no origination share — should they be a working attorney only?" — as a soft check you can answer with a reason rather than a hard block.',
    ],
  },
  {
    version: '1.42.1',
    date: '2026-08-19',
    highlights: [
      'The same attorney can no longer be picked twice in the Origination Split — each row omits whoever is already chosen elsewhere, and a duplicate that slipped in earlier is flagged.',
    ],
  },
  {
    version: '1.42.0',
    date: '2026-08-19',
    highlights: [
      'An origination row left on "Select attorney…" no longer counts as complete — 75/25/0/0 with two unselected rows used to pass because it added up to 100%.',
      'New auto-fill rule: "Copy people into another field (add only)" — put Working Attorneys into the Origination Split automatically, without removing anyone when they come off the working list.',
      'Validation errors now appear on the page that actually holds the field, even after you move fields between pages.',
    ],
  },
  {
    version: '1.41.0',
    date: '2026-08-19',
    highlights: [
      "The Origination Split's dropdowns can now be limited by title like the other people pickers — attorneys only, instead of the whole roster.",
      'The "+ Add working attorney" button can be relabelled to match how the section is used.',
    ],
  },
  {
    version: '1.40.0',
    date: '2026-08-18',
    highlights: [
      "PDF Layout: a “Match the form's side-by-side layout” toggle makes the printed record mirror the form's grid — fields that share a line on the form share a line on the PDF, re-flowing per matter as conditional fields appear or disappear.",
    ],
  },
  {
    version: '1.39.0',
    date: '2026-08-18',
    highlights: [
      'Inserting a people picker now offers the "Limit to titles" filter right in the Add field form — scope it to attorneys, legal secretaries, paralegals, etc. as you create it.',
      'New "People (pick one)" field type for single-person picks like "Assigned legal secretary."',
    ],
  },
  {
    version: '1.38.1',
    date: '2026-08-18',
    highlights: [
      'The two General Counsel roster rows import with their full "Partner, General Counsel" title (another unquoted comma in the feed), and title filters now match each part of comma-combined titles.',
    ],
  },
  {
    version: '1.38.0',
    date: '2026-08-18',
    highlights: [
      'Legal secretaries (and all support staff) now import correctly from the roster feed — their unquoted "LAST, FIRST" names were shifting columns and mangling 92 of 455 rows.',
      'Roster people now carry a job title, and every people chooser can be limited by title in Field Builder — e.g. a picker that only offers Legal Secretaries.',
    ],
  },
  {
    version: '1.37.2',
    date: '2026-08-18',
    highlights: [
      'The "System-generated…" tagline in the PDF header can now be hidden entirely, independently of the numbers line — any combination prints cleanly.',
    ],
  },
  {
    version: '1.37.1',
    date: '2026-08-18',
    highlights: [
      'The "Client No. / Matter No." line in the PDF header is now editable ({clientNumber} and {matterNumber} print the real values) with an Auto / Always / Never visibility control — Auto hides it only while the workflow is off and nothing is assigned.',
    ],
  },
  {
    version: '1.37.0',
    date: '2026-08-18',
    highlights: [
      'PDF Layout: the title block is editable too — click the firm name, document title, or tagline at the top of the page to reword how the header prints.',
    ],
  },
  {
    version: '1.36.0',
    date: '2026-08-18',
    highlights: [
      'PDF Layout: click any printed label or page heading to reword how it prints — PDF-only, the form is unchanged, and blanking the text restores the original wording.',
    ],
  },
  {
    version: '1.35.0',
    date: '2026-08-18',
    highlights: [
      'The columns inside built-in repeating groups (Opposing parties, Adverse parties, …) can now be renamed, added, or removed in Field Builder — with a one-click restore of the originals. A group left with one column spans the full row.',
    ],
  },
  {
    version: '1.34.0',
    date: '2026-08-18',
    highlights: [
      'The field outline is back — but now it is an admin setting: thin or thick, in your chosen colour, and you pick whether it appears only after a failed submit or whenever the field is empty. Off by default.',
    ],
  },
  {
    version: '1.33.0',
    date: '2026-08-17',
    highlights: [
      'Admin → Field rules can now set the symbol and colour used to mark required and soft-required fields — twelve symbols (including "no marker") and eight colours, with a live preview.',
    ],
  },
  {
    version: '1.32.2',
    date: '2026-08-17',
    highlights: [
      'Required fields no longer get a red outline just for being blank — the asterisk marks them, and red only appears once you try to submit.',
    ],
  },
  {
    version: '1.32.1',
    date: '2026-08-17',
    highlights: [
      'Field Builder is much easier to use: the Help text and prompt boxes are wide enough to actually type in, the checkbox columns no longer hog space, and each field takes up less vertical room.',
    ],
  },
  {
    version: '1.32.0',
    date: '2026-08-17',
    highlights: [
      'You can now change a custom field\'s type right on its row in Field Builder, instead of deleting and re-adding it — which keeps its saved answers, visibility rules and PDF placement intact.',
    ],
  },
  {
    version: '1.31.0',
    date: '2026-08-17',
    highlights: [
      'New "Percentage" field type — set its minimum, maximum and allowed decimal places per field in Field Builder.',
      'Dropdown and people-picker prompts like "Select attorney…" are now editable per field instead of hardcoded.',
    ],
  },
  {
    version: '1.30.0',
    date: '2026-08-17',
    highlights: [
      'The condition builder can now check "has been answered" / "has not been answered yet," so a field can stay hidden until an earlier dropdown is actually touched, not just until it matches one specific answer.',
    ],
  },
  {
    version: '1.29.2',
    date: '2026-08-17',
    highlights: [
      '"My matters" now always shows just your own submitted matters plus anything shared with you — admins and approvers no longer see every matter firm-wide on this page (use the Approval Queue or Workflow Tracking for that).',
    ],
  },
  {
    version: '1.29.1',
    date: '2026-08-17',
    highlights: [
      'Every field in Field Builder — including the original built-in fields, not just custom ones — can now have conditional visibility.',
      'Field Builder\'s field rows are more compact, fitting more on screen at once.',
    ],
  },
  {
    version: '1.29.0',
    date: '2026-08-17',
    highlights: [
      'New "Auto-fill rules" tab on Field Builder: set a field\'s default once a condition is true, mirror one field into another, or look up a related record and copy an attribute of it — no code change needed.',
    ],
  },
  {
    version: '1.28.1',
    date: '2026-08-16',
    highlights: [
      'Fixed a bug where pairing two fields onto one line in PDF Layout would push every field printed after it out of alignment.',
      'Fixed decision comments sometimes printing twice on the Approval Trail.',
      '"GC Approval Required" now shows a plain Yes/No instead of an always-checked box.',
    ],
  },
  {
    version: '1.28.0',
    date: '2026-08-16',
    highlights: [
      'Every field in PDF Layout can now be hidden from the printed record (with a "Show hidden fields" list to bring one back) — the live form is unaffected either way.',
      'Notice banners no longer print on the record by default — they\'re instructions for filling out the form, not an answer.',
    ],
  },
  {
    version: '1.27.1',
    date: '2026-08-16',
    highlights: [
      'PDF Layout now actually looks like the printed page — real page proportions, matching typeface, and real sample values instead of placeholder text.',
      'Every field paired onto a shared line now has a visible "Own line" button to split it back out.',
      'The completion-email and final-package distribution lists moved off "Areas of law" (renamed "Areas of law & subtypes") to Workflow and Numbering, where they actually belong.',
    ],
  },
  {
    version: '1.27.0',
    date: '2026-08-16',
    highlights: [
      'The admin nav is decluttered — Conflicts notification and Conflicts page are now one "Conflicts" tab, and Test mode lives on the Workflow page.',
      'PDF Layout is now a full visual editor: drag fields side by side, resize them, format text, and preview the real PDF before saving.',
    ],
  },
  {
    version: '1.26.0',
    date: '2026-08-16',
    highlights: [
      'New: a drag-and-drop PDF layout editor (Admin → PDF Layout) — print the matter-record PDF in a different order than the live form, without changing the form itself.',
    ],
  },
  {
    version: '1.25.0',
    date: '2026-08-16',
    highlights: [
      'Workflow steps can now be a one-way notification or a mid-flow data pickup, not just an approval — e.g. route Wall Builder Designation to Records to fill in, or send Files to be Opened to someone the moment a matter is submitted.',
      'Admin → Workflow has a new "Add step" button, with a Kind selector and a field picker for the new step types.',
    ],
  },
  {
    version: '1.24.2',
    date: '2026-08-16',
    highlights: [
      'The admin nav and settings page now say "Workflow" instead of "Approval Flow" — a step in this system can now be a decision, not just an approval.',
    ],
  },
  {
    version: '1.24.1',
    date: '2026-08-16',
    highlights: [
      'With the approval workflow turned off, nothing in the app mentions "approval" or "workflow" anymore — the submission email, the matter-record PDF, and the Matter Detail page all reflect that the feature is off.',
    ],
  },
  {
    version: '1.24.0',
    date: '2026-08-15',
    highlights: [
      'The standalone Conflicts request page can now be edited in place — headings, help text, and party-list columns — the same way the intake form is, from an "Edit layout" button admins see on the live page.',
      'The party list defaults to just a list of names; admins can add extra columns (entity type, role, notes, or anything else) if Conflicts needs more.',
    ],
  },
  {
    version: '1.23.1',
    date: '2026-08-15',
    highlights: [
      'Fixed a crash that blanked the whole page when submitting or verifying a matter with any field marked “Verification required.”',
    ],
  },
  {
    version: '1.23.0',
    date: '2026-08-14',
    highlights: [
      'The matter-record PDF now mirrors the form as it actually looked: admin page order and labels, custom pages and fields (including repeating groups), and anything hidden or deleted no longer appears.',
      'The record is friendlier to read — normal sentence-case headings instead of all caps, and empty pages no longer print at all.',
      'The approval trail now shows whether General Counsel sign-off was required for this matter.',
    ],
  },
  {
    version: '1.22.0',
    date: '2026-08-14',
    highlights: [
      'Approvers now see admin-added custom fields. Anything added in Field Builder — including repeating groups like an additional-contacts table — now appears in an “Additional information” section on the approval page, so approvers see everything they are signing off on.',
      'Those values read the same way on the approval page, the intake form, and the matter-record PDF (choice labels, Yes/No, people by name).',
    ],
  },
  {
    version: '1.21.0',
    date: '2026-08-14',
    highlights: [
      'New Admin → Conflicts page: all of the wording on the standalone conflict-check request page is now editable — headings, intro, help text, buttons and the confirmation message. Clearing a box restores its default.',
      'The matter description on that page can now be shown, hidden, or made required. A required description is enforced on the server too, not just in the browser.',
    ],
  },
  {
    version: '1.20.0',
    date: '2026-08-14',
    highlights: [
      'Pages can now have their own show/hide logic: “show this page only when X is Y”, “Always show this page”, or reset to the page’s default — from Admin → Field builder or Edit layout on the form.',
      'This means a page that shipped tied to a built-in field (like Litigation, tied to Matter Type) can be re-pointed or always shown if that field is removed, instead of disappearing for good.',
    ],
  },
  {
    version: '1.19.0',
    date: '2026-08-14',
    highlights: [
      'Notice banners can now be formatted: bold, italic, underline, and four text sizes — from Admin → Field builder or Edit layout on the form. The banner colour still comes from its Warning / Neutral / Plain style.',
      'Field labels gained the same italic, underline and size options.',
    ],
  },
  {
    version: '1.18.0',
    date: '2026-08-14',
    highlights: [
      'Admin → Field builder now offers Repeating group: define the columns once and end users get an “+ Add another…” button — the way to add things like a second client contact or extra parties, with no deploy.',
      'Each entry appears as its own labeled card (Contact 1, Contact 2) with its fields grouped together and its own Remove button.',
      'Repeating entries now read properly in the matter record PDF, the consistency review, and the pre-submit verification list, using your column labels.',
      'Adding a blank entry no longer counts as answering a required field.',
    ],
  },
  {
    version: '1.17.0',
    date: '2026-08-14',
    highlights: [
      'Built-in form fields can now be deleted, not just hidden — from Edit layout on the form or Admin → Field builder. Deleted built-ins move to a “Deleted fields” panel in the Field Builder with one-click Restore; saved answers are never erased.',
      'Deleting a field that an approval rule or a show/hide condition depends on now asks for confirmation naming exactly what it affects.',
      'Notices can now be Plain — no border or background — in addition to Warning and Neutral.',
      'All form text is now editable: page descriptions can be changed or cleared, and help text can be edited right on the form in Edit layout — joining labels, page titles, notices, and choices.',
      'When the approval workflow is turned off, the submit button now reads “Send / Download PDF”.',
    ],
  },
  {
    version: '1.16.0',
    date: '2026-08-14',
    highlights: [
      'Dropdown, choice, and checkbox-list fields can now have their choices edited after they are created — including built-in fields like Client Source — from both Edit layout on the form and Admin → Field builder.',
      'Fields can now be formatted: bold label, larger label, background color (gray, blue, or amber highlight), and a boxed border. Available in both editors under “Choices & formatting.”',
    ],
  },
  {
    version: '1.15.0',
    date: '2026-08-14',
    highlights: [
      'Administrators can now edit the form in place: an Edit layout button on the intake form lets you rename fields, set their width, change requirement, move them between pages, hide/show them, and delete custom fields — with a live preview, saved only when you click Save layout.',
      'Admin → Field builder now offers the same Full / Half / Third width choice for every field, replacing the old “Span entire line” checkbox.',
    ],
  },
  {
    version: '1.14.0',
    date: '2026-08-14',
    highlights: [
      'City, State and ZIP now sit together on one line in the client address block, making the form more condensed.',
      'Form fields can now be laid out at full, half, or third width — the groundwork for finer control over how compact each section looks.',
    ],
  },
  {
    version: '1.13.2',
    date: '2026-08-14',
    highlights: [
      'Fixed: a mistyped operator in an approval-trigger rule no longer accidentally adds an approval step. The rule now safely does not fire, and the typo is flagged as a warning on the matter (and in the audit log) so an admin can correct it.',
    ],
  },
  {
    version: '1.13.1',
    date: '2026-08-14',
    highlights: [
      'Admin → Field builder’s Custom list (multiple) field is now admin-defined: admins add the list of choices in Field Builder, and end users pick from that list on the form as removable chips — no more free typing.',
    ],
  },
  {
    version: '1.12.2',
    date: '2026-08-14',
    highlights: [
      'The People (multiple) picker now has a search box and a scrollable list when adding people, instead of showing the entire roster inline.',
    ],
  },
  {
    version: '1.12.1',
    date: '2026-08-14',
    highlights: [
      'The header navigation now lines up with the intake form column, making the app’s primary navigation easier to scan alongside the form.',
      'The intake sidebar’s completion count stays visible at the bottom while you move through a long form, and Field Builder keeps Save field layout with its save feedback within reach while you scroll.',
    ],
  },
  {
    version: '1.12.0',
    date: '2026-08-14',
    highlights: [
      'Admin → Field builder now offers People (multiple), so you can add a roster-backed multi-person picker anywhere in the intake form. It works like Practice groups: selected people appear as removable chips and “+ Add person” lists remaining people by name.',
      'People selected in a custom People (multiple) field now appear by name in verification, consistency review, and completed matter records. Role filtering is deferred because the current roster feed does not include attorney, paralegal, or staff roles.',
    ],
  },
  {
    version: '1.11.0',
    date: '2026-08-14',
    highlights: [
      'Administrators can now set the application name in Admin → Approval flow → Application and intake settings. The header uses that name immediately; a blank value safely returns to “New Matter Intake.”',
      'The version pill now sits next to the dark-mode toggle. The intake form no longer repeats the application name as a page heading, and its applicable-sections completion count now sits below the sidebar section list.',
    ],
  },
  {
    version: '1.10.0',
    date: '2026-08-14',
    highlights: [
      'Admin → Approval flow now includes an Enable Review for consistency setting. It is on by default; when it is turned off, the optional review action is removed from the intake form.',
      'Completed matter records can now be downloaded from the matter detail page by the submitter or an administrator. Downloads use a newly generated short-lived secure link rather than exposing a permanent file URL.',
    ],
  },
  {
    version: '1.9.0',
    date: '2026-08-14',
    highlights: [
      'Matter lists now offer safe single and multi-select deletion. Your own drafts can be deleted, while administrators can delete any matter; every deletion requires a clear final confirmation.',
      'Deleting a matter now clears its approval records, attachment metadata, and uploaded or generated files together. If a mixed selection contains a matter you cannot delete, the permitted matters are removed and the result explains what was skipped.',
    ],
  },
  {
    version: '1.8.14',
    date: '2026-08-14',
    highlights: [
      'Files To Be Opened now uses a roomier responsive checkbox grid. Wrapped labels keep a clean, even rhythm instead of crowding neighboring choices.',
      'Admin → Field builder now includes “Span entire line” beside “Start this field on a new line” for existing fields and new custom fields. It persists with the layout and can override a shipped full-width default.',
    ],
  },
  {
    version: '1.8.13',
    date: '2026-08-14',
    highlights: [
      'Admin → Field builder now offers “Start this field on a new line” for both existing editable fields and newly added custom fields. It is saved with the field layout and can be switched off again at any time.',
      'Conditional answers no longer have to visually share a row just because of grid order. Client Source now places the receivable question and its Amount outstanding answer on clear separate rows.',
    ],
  },
  {
    version: '1.8.12',
    date: '2026-08-14',
    highlights: [
      'Admin → Field builder: the signed-engagement-letter and conflict notices, plus every upload block, can now be moved, shown or hidden, and edited like other fields. Notice messages can use a warning or neutral tone; existing upload categories remain safely fixed.',
      'Administrators can now add their own notice banners and named attachment areas. New attachment categories are slugified and must be unique, so they cannot collide with the engagement-letter workflow or another upload bucket.',
      'Approval attachment headings now follow the effective Field Builder labels, including new custom upload areas, instead of relying on a separate hardcoded list.',
    ],
  },
  {
    version: '1.8.11',
    date: '2026-08-14',
    highlights: [
      'Admin → Field builder: a newly added page now scrolls into view and briefly highlights itself, and its title field is focused automatically — no more hunting for it at the bottom of a long page list.',
      'Admin → Field builder: every field now has a "Move to page" dropdown as a no-drag alternative to dragging a field between pages.',
    ],
  },
  {
    version: '1.8.10',
    date: '2026-08-14',
    highlights: [
      'Admin → Field builder can now add a new custom page, rename/reorder/retire it, and place new or movable fields on it just like any built-in page.',
      'Empty custom pages can now be deleted with an explicit confirmation. Built-in pages are kept safe: they cannot be deleted and instead show the existing Show page retirement control.',
      'Custom-page fields now appear in Field Rules and in the AI consistency-review context, so the administrative layout stays consistent across intake and review tools.',
    ],
  },
  {
    version: '1.8.9',
    date: '2026-08-14',
    highlights: [
      'Conflict Check now always shows its info in full instead of starting collapsed for existing clients — the section no longer needs to be expanded to see conflict details.',
    ],
  },
  {
    version: '1.8.8',
    date: '2026-08-14',
    highlights: [
      'Origination of File / Working Attorneys now defaults a newly added attorney’s percentage to whatever remainder is left to reach 100% (e.g. 75% then 25%) instead of always starting at 0%, and the running total is now shown above the percentage boxes so it’s easy to notice while filling them in.',
      'The Shared With panel on the intake form is now a compact, muted bar instead of a bold white card, so it reads as a secondary utility rather than another required form section.',
    ],
  },
  {
    version: '1.8.7',
    date: '2026-08-13',
    highlights: [
      'Additional Practice Groups on Header & Billing now stay tucked behind a "+ Add practice group" button instead of always listing every group, so the page stays compact when the list isn’t needed.',
      'Choosing a different main Practice Group on Header & Billing now always carries the previous and new choice into Wall Builder access as additions, never removals — matching how Additional Practice Groups already behaved. Wall Builder edits still never flow back to Header & Billing.',
    ],
  },
  {
    version: '1.8.6',
    date: '2026-08-13',
    highlights: [
      'Header & Billing now has Additional Practice Groups: choose any active secondary practice groups with the same clear chip-and-remove interaction used for Wall Builder access.',
      'Wall Builder now uses that same shared chip picker. Adding an additional practice group selects it there once; later removals stay one-directional, so a person’s deliberate Wall Builder edits are never overwritten.',
      'Additional Practice Groups also appear in the completed matter-record PDF and are included in the AI consistency review context.',
    ],
  },
  {
    version: '1.8.5',
    date: '2026-08-13',
    highlights: [
      'Administrators can now choose whether each intake page must be complete before a person continues. The requirement stays on by default; when turned off, Next and sidebar page jumps remain available while the existing completion checkmarks and progress count still show what is incomplete.',
      'Administrators can now set the maximum origination percentage for one attorney or paralegal (75% by default). The form immediately limits every share to that maximum, and submission still requires all listed shares to total exactly 100%.',
    ],
  },
  {
    version: '1.8.4',
    date: '2026-08-13',
    highlights: [
      'Administrators can now set the single email address for Conflict Check notifications in Admin → Conflicts notification. Until an address is saved, requests keep using conflictchecks@aalrr.com.',
      'Conflict Check notifications still honor Test mode: the configured address is retained as the intended recipient, while Test mode safely redirects the outgoing message to its test inbox.',
    ],
  },
  {
    version: '1.8.3',
    date: '2026-08-13',
    highlights: [
      'Custom fields can now be shown only when another answer matches. In Admin → Field builder, open a custom field’s Conditional visibility editor, choose a field from any intake page, then choose its matching answer. The existing visual rule builder supports one rule or grouped AND/OR rules.',
      'Conditional custom answers stay out of the saved matter and its record PDF whenever their rule no longer applies. Approval-flow routing keeps its established, focused field picker.',
    ],
  },
  {
    version: '1.8.2',
    date: '2026-08-13',
    highlights: [
      'Admin → Field builder can now add a Choice (pick one) radio field to any page. Give each choice a display label and stored value; the selected label also appears in the completed matter-record PDF.',
      'Next now explains which required answers need attention instead of letting you continue past an incomplete page. The sidebar also prevents forward jumps past incomplete applicable pages, while you can always go back. Pages that do not apply to your answers never hold up progress.',
    ],
  },
  {
    version: '1.8.1',
    date: '2026-08-13',
    highlights: [
      'Admin → Field builder drag and drop is now dependable across pages: place a field in the middle of another page or move it into an empty page, while page reordering no longer competes with nested field rows. The precise up/down controls remain available.',
      'Choosing Other for Area of Law now shows two separate answers: what the area is and why it is categorized as Other. Both answers stay separate in the matter record and its PDF.',
    ],
  },
  {
    version: '1.8.0',
    date: '2026-08-13',
    highlights: [
      'A focused Conflict Check page is now available at /conflicts. It has no main-app navigation and lets you send names or entities, their roles or notes, and a brief matter description directly to the Conflicts team.',
      'Conflict Check requests are separate from New Matter Intake: they do not create a matter, approval route, number, or PDF. The page uses your signed-in name and email automatically.',
    ],
  },
  {
    version: '1.7.0',
    date: '2026-08-13',
    highlights: [
      'Admin → Field builder now supports drag-and-drop page and field ordering. Drag a field to another page when the intake flow makes more sense there, or keep using the existing up/down arrows for a precise adjustment.',
      'Page titles are editable in the builder, and a page can be temporarily retired without deleting it or its fields. Pinned notices and shared attachment blocks stay safely in their intended context.',
    ],
  },
  {
    version: '1.6.0',
    date: '2026-08-13',
    highlights: [
      'Administrators can now turn approval routing on or off from Admin → Approval Flow. It stays on by default. When it is off, a new matter is submitted directly and you receive its complete matter record as a PDF instead of sending it through approvers.',
      'When approval routing is off, My Approvals and Routing are hidden from navigation, the intake action says “Submit matter,” and Intake can assign numbers to directly submitted matters just as it can to fully approved ones.',
    ],
  },
  {
    version: '1.5.0',
    date: '2026-08-13',
    highlights: [
      'Area of Law is now a straightforward single-choice dropdown. The list is still maintained from Admin, and choosing “Other” still opens the description field right underneath.',
      'Previously submitted matters that selected more than one area remain fully readable in approval views, emails, and their matter-record PDFs. No historical records need to be changed.',
    ],
  },
  {
    version: '1.4.0',
    date: '2026-08-13',
    highlights: [
      'You can now choose “Review for consistency” while preparing a matter. It reads the form as a whole and points out possible genuine contradictions between answers or between a narrative and structured fields. The same informational review runs before submission; when it finds something, you can consider it and explicitly continue anyway.',
      'When the firm later configures its standard engagement-letter text, the first approver’s existing approval email will call out substantive AI-detected differences in the attached letter, such as fee, scope, or major-clause changes. It does not add a new approval gate, and until that template is configured it has no effect on today’s emails.',
    ],
  },
  {
    version: '1.3.48',
    date: '2026-08-11',
    highlights: [
      'New client and New matter now both start unchecked on a fresh draft, so the intake preparer makes an explicit choice instead of the form silently assuming both by default.',
      'Removed the "Initials of Managing Partner" field from the engagement letter waiver — that approval already happens for real through the Engagement Letter Waiver approval step, so no separate sign-off is needed on the form. The reason for waiving is still captured and shown to the Managing Partner during approval.',
    ],
  },
  {
    version: '1.3.47',
    date: '2026-08-11',
    highlights: [
      '“Save as template” now uses the same clear secondary-button styling as other form actions, making it easier to identify as a clickable action.',
      'The Shared with card now appears below the active form section. It stays expanded when a matter already has collaborators, but otherwise starts compact and opens in place when you choose “+ Add.”',
      'For a new matter for an existing client, the form now conveniently defaults the client source to Current client and the two Prior Representation answers to No when those answers have not yet been set. You can still choose any other answer.',
      'Conflict Check now follows the client name until you enter a different search name, and starts collapsed for existing clients without removing any required fields or validation. Selecting a practice group also initially adds it to the empty Wall Builder access list.',
    ],
  },
  {
    version: '1.3.46',
    date: '2026-08-11',
    highlights: [
      'Removed the disabled “Select from iManage (coming soon)” placeholder from the engagement-letter upload control. Drag-and-drop and Browse files remain the only ways to attach a file for now; a real iManage picker can be added later when that integration is ready.',
      'Added a proper browser tab icon (favicon) for the app, replacing the generic/blank icon shown previously.',
    ],
  },
  {
    version: '1.3.45',
    date: '2026-08-10',
    highlights: [
      'My Approvals is available again to signed-in people who are eligible to act on a pending approval. The queue now has the Firestore permission it needs to list that work.',
      'Administrators can now skip a pending approval step when needed. Skip requires a written reason, is visibly recorded as skipped in the workflow history, and moves the matter to the next approval step just like a completed decision.',
      'The development environment now includes the same Private Labor & Employment and Southern California Education Law practice-group IDs used by General Counsel routing, while retaining the older sample groups for compatibility.',
      'Fixed signed engagement-letter uploads: the chosen PDF, DOC, or DOCX is now retained while the file picker resets, then appears in the engagement-letter attachment list as expected.',
      'Approval-email review links continue to open the intended /approve action route; this behavior was confirmed and was not changed.',
    ],
  },
  {
    version: '1.3.44',
    date: '2026-08-10',
    highlights: [
      'The intake form now adapts more closely to your answers. It skips and clears fields that do not apply — including existing-client payment questions for a new client, hourly/estimated fee details for flat-fee work, and entity details when no party is an entity. The form also shows “X of N applicable sections complete” and no longer prefixes its changing section list with misleading numbers.',
      'When you confirm that a signed engagement letter is on file, you can now drag in or browse for a PDF, DOC, or DOCX directly there. The normal attachment history, download, and removal controls still apply. “Select from iManage (coming soon)” is visible as a disabled placeholder; no iManage connection is attempted yet.',
      'Intake can now record whether the Aderant conflict report found hits from the matter page while its Conflict Check is pending. That approval cannot be completed until this result is explicitly saved, preventing a blank result from passing unnoticed.',
      'Submitting with missing answers now takes you to the first affected section and explains what needs attention. Fields that are usually supplied must be completed or explained before submission, and “Opposing counsel (if known)” and “Court” now follow that explain-if-blank pattern rather than appearing as absolute requirements.',
      'The verification dialog no longer blocks submission when no fields need verification. My Approvals works for a signed-in eligible approver without an email link, Matter Detail uses readable names and current audit history, and the numbering email opens the correct Intake queue.',
    ],
  },
  {
    version: '1.3.43',
    date: '2026-08-06',
    highlights: [
      'The new Parties section is now the first section of every intake. Use it to list every individual or entity that needs a conflict review, with its type, role, and any helpful notes. Those names now also appear for the Intake conflict review and in the approved matter-record PDF.',
      'Conflict Check now records whether the search found any hits. When it did, a clear warning requires the person completing the form to alert the Requesting Attorney and confirm that acknowledgement before the matter can be submitted.',
      'Routing no longer offers Remind on a recalled matter, even if a historical approval step still says it is pending. When an old roster record is unavailable, Routing now says “Unknown user” instead of showing a raw system ID.',
    ],
  },
  {
    version: '1.3.42',
    date: '2026-08-06',
    highlights: [
      'Routing now has a “Remind” action beside any approval step that is actively waiting for a decision. It immediately sends that approver the same reminder email and a fresh review link, instead of waiting for the scheduled reminder sweep.',
      'A new “Reminder Sender” role can be assigned from Admin → Roster & roles. It is separate from Administrator and Workflow Viewer: holders can reach Routing and send these targeted reminders, but receive no approval, conflict-check, numbering, or other admin rights.',
      'Manual reminders still update the step’s reminder history and pause the automatic cadence, but they never use up the scheduled escalation allowance or cause a matter to escalate just because someone sent a nudge.',
    ],
  },
  {
    version: '1.3.41',
    date: '2026-08-06',
    highlights: [
      'The header now keeps your name under the New Matter Intake title, rather than using space beside the navigation. If you have more than one role, select your name to see the full role list in a small menu.',
      'Dark mode is now available from the sun/moon button in the header. Your first visit follows your device setting; once you choose light or dark mode, the app remembers that choice. Every app page, including forms, tables, workflow views, and admin tools, now has a matching dark appearance.',
    ],
  },
  {
    version: '1.3.40',
    date: '2026-08-06',
    highlights: [
      'Removed the "General Counsel approval required?" manual toggle from the intake form. It had no effect on anything — General Counsel approval has been driven entirely by the practice group / new-client / litigation rules (since 1.3.36) — so the field was just a confusing leftover checkbox. The matter record PDF still shows a "General Counsel Approval Required" line; it now reflects the real, current approval chain instead of the old manual answer.',
      'The Verify step before submitting a matter now shows "Who will approve this" — the exact list of approval steps and approvers your matter will route to, computed the same way the real approval engine will, before you commit to submitting. No more submitting and only then finding out who is in the chain.',
      'Approvers can now reject a matter back to whoever most recently approved it, instead of only being able to reject all the way back to the original requestor. When at least one earlier step has already been approved, the approval page offers a choice: "Reject to requestor" (the original, still-default behavior — ends the matter) or "Reject to [last approver]" (reopens that person\'s approval for a fresh look, with everything after them reset). The person being routed back to gets a "please re-review" email explaining what sent it back; the submitter gets an informational notice, not a rejection notice. Any approval links already sent to steps that got reset stop working, the same way an approval link always stops working once its step is no longer waiting on a decision.',
    ],
  },
  {
    version: '1.3.39',
    date: '2026-08-05',
    highlights: [
      'Fixed “Recall matter” from 1.3.38, which could never actually be used: submitting a matter always activates its first approval step right away (the approver gets emailed as part of submission), and the recall button was mistakenly treating that as “approval already in progress.” Recall is now correctly available up until an approver actually makes a decision — approves, rejects, or requests more information — not merely once they have been notified.',
    ],
  },
  {
    version: '1.3.38',
    date: '2026-08-05',
    highlights: [
      'New “Recall matter” button on the matter page, next to “Clone this matter”. It pulls a submitted matter back out of the approval workflow when it was sent off with a mistake — the wrong responsible partner, the wrong practice group, a missing attachment — instead of letting it run the whole chain or asking an administrator to edit the database by hand.',
      'Who can recall: the attorney or secretary who submitted the matter, or an administrator acting on their behalf.',
      'When recall is allowed: only while approval has not started moving. Every step in the chain must still be waiting — as soon as a single step has been activated (its approver emailed) or decided (approved, rejected, or more information requested), the button disappears and the recall is refused. Nothing that an approver has already seen can be pulled back.',
      'What recall does: the matter moves to a new read-only “Recalled” status. It is not returned to an editable draft, and nothing is deleted — the approval chain and the full history stay on the record for audit. To carry on, use “Clone this matter” on the recalled matter: that opens a fresh, unsubmitted draft pre-filled from it, which you can correct and submit as normal.',
      'Admin → Workflow tracking can now filter on the Recalled status.',
    ],
  },
  {
    version: '1.3.37',
    date: '2026-08-05',
    highlights: [
      'Submitting attorneys are now emailed as soon as their client and matter numbers are assigned. The email states both numbers and attaches the complete, numbered matter record PDF. Previously the only email the submitter received was the “fully approved” notice, which is sent before numbering happens — so there was no automatic way to find out the assigned numbers.',
      'This new notice is sent once per matter: if Intake re-saves the numbers (for example to correct a typo), the submitter is not emailed again. Admins can still resend any message from Admin → Mail Queue.',
      'Fixed: when no final distribution list is configured in Admin → Settings, the numbering step used to send no email at all. The submitter’s new numbers notice now goes out regardless of whether a distribution list is configured.',
    ],
  },
  {
    version: '1.3.36',
    date: '2026-08-05',
    highlights: [
      'General Counsel approval is no longer requested on every matter. It is now only required for the four private-sector practice groups — Northern California Education Law, Southern California Education Law, Student Services and Disabilities Law, and Private Labor and Employment — and within those, only for new clients, or for existing clients when the matter is litigation. Public-entity group matters no longer go to the General Counsel at all.',
      'The Practice Group Leader step is now called “Practice Group Leader Sign-Off” instead of “Practice Group Leader Final Sign-Off”, since General Counsel approval can still follow it.',
      'Admin → Approval Flow: the condition builder can now build multi-condition rules visually. Add several conditions to a rule group (all must match), and add alternative rule groups (any group matching is enough) — no more hand-editing JSON. You can now also branch a step on whether the client is new or existing, and on whether the matter is litigation.',
      'Admin → Approval Flow: the Preview panel now lets you pick a practice group and toggle “New client”, so you can see exactly when the General Counsel step applies.',
      'Note for admins: an existing saved approval flow keeps its own copy of the trigger table, so the new General Counsel rule needs to be applied once to live data (either by the one-off migration script or by re-saving the flow from Admin → Approval Flow).',
    ],
  },
  {
    version: '1.3.35',
    date: '2026-08-05',
    highlights: [
      'Fixed: approval, reminder, and numbering links in email now point to the real app instead of a dead placeholder address.',
    ],
  },
  {
    version: '1.3.34',
    date: '2026-08-05',
    highlights: [
      'Fixed: the Mail Queue tab now shows the actual recipient an email was sent to — including when test mode redirected it — instead of always showing the original intended recipient. The original recipient is still preserved and shown alongside it for reference.',
    ],
  },
  {
    version: '1.3.33',
    date: '2026-08-04',
    highlights: [
      'Fixed: the Test Mode settings page could show the toggle as off and the test email as blank even when test mode was actually enabled (the banner was always correct — the settings form just wasn\'t syncing with live data). It now always reflects the current saved state unless you have unsaved edits in progress.',
    ],
  },
  {
    version: '1.3.32',
    date: '2026-08-04',
    highlights: [
      'Roster CSV import now honors an Inactive column: Y marks the person inactive, N (or blank) keeps them active.',
    ],
  },
  {
    version: '1.3.31',
    date: '2026-08-04',
    highlights: [
      "Fixed: the Shared with card on the intake form now appears as soon as your draft has been saved at least once — including a brand-new matter you're actively composing (previously it only appeared after reopening an already-saved draft). Also moved it near the top of the form, right under the title, so it's easier to find.",
    ],
  },
  {
    version: '1.3.30',
    date: '2026-08-04',
    highlights: [
      "You can now share a matter while it's still a draft (previously the Shared with card only appeared after submission) — look for the Shared with card on the intake/edit page for any draft you've already saved at least once.",
    ],
  },
  {
    version: '1.3.29',
    date: '2026-08-04',
    highlights: [
      "Roster CSV imports now capture each person's Employee Code, shown in Admin > Roster and searchable there and anywhere you search for a person (e.g. when sharing a matter).",
    ],
  },
  {
    version: '1.3.28',
    date: '2026-08-04',
    highlights: [
      'Responsible Partner is now a single value used everywhere — the Origination section\'s copy always matches Header & Billing and can no longer be set independently. The Origination field still shows the selected person, but changing the Responsible Partner is now done in Header & Billing only.',
    ],
  },
  {
    version: '1.3.27',
    date: '2026-08-04',
    highlights: [
      'Added a "Test mode" for outbound email (Admin → Test mode): when turned on, every outgoing email — approval requests, reminders, escalations, submitter updates, final distribution, everything — is redirected to one test address instead of real staff/attorneys/clients, with a clear [TEST MODE] subject tag and a banner showing who it would really have gone to. A persistent amber warning banner shows for admins anywhere in the app while it\'s on, so it doesn\'t get left on by accident.',
    ],
  },
  {
    version: '1.3.26',
    date: '2026-08-04',
    highlights: [
      'Fixed a crash on the New Matter Intake page that could happen right after the recent verification-checklist update.',
    ],
  },
  {
    version: '1.3.25',
    date: '2026-08-04',
    highlights: [
      'Tidied the header into a single line at normal desktop widths: the "Workflow Tracking" nav link (the one causing the wrap) is now labeled "Routing", and the nav/user area no longer wrap onto a second row.',
      'Workflow Tracking now shows an "Owner" column (the matter’s submitter) and includes the owner’s name in the existing search box.',
      'Matters can now be shared with specific colleagues: a new "Shared with" card on the matter detail page lets the owner (or an admin) add/remove people from the roster. Shared people can view the matter (and edit it while it is still a draft); "My matters" now has a "Shared with me" section for matters someone else shared with you.',
      'Field Builder has a new "Verification required" checkbox per field, alongside "Never copy." Before submitting a matter for approval, a "Please verify the following information" checklist now lists every visible field flagged this way with its current value, and requires each one to be checked off before "Submit for approval" is enabled.',
    ],
  },
  {
    version: '1.3.24',
    date: '2026-08-04',
    highlights: [
      'Actual Hourly Rate Quoted to Client now accepts free text (e.g. standard rates, a range, or partner/associate rates) instead of only a plain dollar amount.',
      'Selecting Litigation or Non-Litigation in Matter Type now correctly clears the "required" error and its red outline right away.',
    ],
  },
  {
    version: '1.3.23',
    date: '2026-08-04',
    highlights: [
      'Client number, matter number, and matter name are now entered exclusively by Intake, at a new /numbering page. This is enforced by the server, not just the UI: admins and superadmins who do not also hold the Intake role can no longer set these fields, and Admin > Numbering now only holds the manual/auto numbering mode toggle plus a note pointing to the new page.',
      'A new "Workflow Viewer" role gives read-only visibility into every matter\'s status, current approval step, and audit history, with no approval, conflict-check, or numbering power. It is a separate role from Intake, grantable per person from Admin > Roster, and can never be assigned as an approver.',
      'A new Workflow Tracking page shows every matter with its current status, current approval step, how many days it has been stuck there, and its eligible approver(s), sorted with the longest-stuck matters first, plus filters (status, practice group, minimum days stuck, search), aggregate stats, and a per-row drill-down into the full audit trail. Visible to admins, superadmins, and the new Workflow Viewer role.',
    ],
  },
  {
    version: '1.3.22',
    date: '2026-08-04',
    highlights: [
      'Roster CSV import now treats the NMMExport CSV as source of truth: anyone previously imported by CSV who is missing from a later import is automatically marked inactive (so they stop appearing in active dropdowns), and reappearing in a future import automatically reactivates them. People who were only ever added manually (never through a CSV import) are never affected by this.',
      'If an import would deactivate more than half of the currently CSV-managed active roster (and there are at least 10 such people), it still goes through, but a very visible warning is added to the import results so an unexpectedly short or wrong CSV doesn\u2019t deactivate everyone unnoticed.',
      'Both the manual CSV import panel and the automated roster feed\u2019s \u201cRun poll now\u201d result in Admin > Roster now show a clear alert listing everyone deactivated by that import, with an explanation of what it means for them.',
    ],
  },
  {
    version: '1.3.21',
    date: '2026-08-04',
    highlights: [
      'The finished-matter PDF attached to completion and final-distribution emails now looks like the firm’s classic paper New Matter Memo form instead of a plain list of fields: a centered title block, a multi-column header grid (Date, Practice Group Leader, GC Approval, Retainer, Responsible Partner, Admin Fee, client/matter numbers), bold dark section banners (Client Information, Nature of Representation, Wall Builder Designation, Client Source, Prior Representation, Conflict Check, Matter Type, Fees, Litigation Hold Letter, Area of Law, Files to Be Opened, Origination/Working Attorneys, Attachments, Approval Trail), side-by-side field pairs, and checkbox-style Yes/No answers. All the same matter data still prints and empty sections are still skipped — it just reads like the familiar paper form now.',
    ],
  },
  {
    version: '1.3.20',
    date: '2026-08-04',
    highlights: [
      'The roster table in Admin > Roster now scrolls inside its own card instead of stretching the whole page — so the Automated roster feed, manual CSV import, Firm Leadership, and Practice Groups sections underneath it are always just below it, no long scroll required.',
    ],
  },
  {
    version: '1.3.19',
    date: '2026-08-04',
    highlights: [
      'Further hardened the roster feed fix below for a same-day case: if one NMMExport email arrives and imports successfully, then a second, different NMMExport email arrives later the same day, that second email now reliably imports too — it’s never blocked just because an earlier email that day was already processed.',
      'The Automated roster feed panel in Admin > Roster now gives a more specific status message when nothing new is found (e.g. how many emails were checked), to make it easier to tell "genuinely nothing new" apart from "something looks off."',
    ],
  },
  {
    version: '1.3.18',
    date: '2026-08-04',
    highlights: [
      'Fixed a bug where the automated roster feed poll could miss a genuinely new SSRS email if an older, already-imported email was also still in its lookback window — previously it only ever checked the single newest email and gave up if that one had already been processed, even when a real new email was sitting right there. It now checks each candidate email in turn and imports the first one that hasn’t already been processed.',
      'Added a “Poll last ran” timestamp to the Automated roster feed panel in Admin > Roster, shown alongside (but separate from) the existing “Last processed email” line — so you can tell the daily poll actually ran today even on a day it found nothing new to import.',
    ],
  },
  {
    version: '1.3.17',
    date: '2026-08-03',
    highlights: [
      'Practice Group Leaders (PGLs) are now set automatically from the daily roster CSV import instead of requiring a manual update in Admin > Roster > Practice Groups. The import reads each row’s PGL/PGLName columns (already present in the SSRS export but previously ignored) and updates the matching practice group’s leader whenever it changes — the manual picker is still there as an override, but day-to-day it should no longer need to be touched. After an import, any practice groups whose leader changed are listed right in the import results.',
      'Fixed a latent bug where three role-placeholder rows in the roster export (“RESPONSIBLE PARTNER”, “PRACTICE G. LEADER”, “L COUNSEL GENERA”, identifiable by their _FOW@ email addresses) were being imported as if they were real employees. They are now correctly excluded from the roster import and are never used as a source for practice group leader information.',
    ],
  },
  {
    version: '1.3.16',
    date: '2026-08-03',
    highlights: [
      'Fixed the ordering of people in every attorney/paralegal dropdown and search box (form fields, the Wall Builder person search, and the approval-flow, roster, and practice-group admin pickers) so they now sort by first name then last name. Previously nothing sorted them at all, which could look like it was grouping by a middle initial (e.g. “Davina F. Harden”) purely by chance — middle names and initials are now ignored for sorting, matching how people actually look someone up.',
    ],
  },
  {
    version: '1.3.15',
    date: '2026-08-03',
    highlights: [
      'Fixed a bug where a failed roster-feed import could get permanently “stuck” and silently block that day’s report from ever being reprocessed. If an import now fails, or is somehow interrupted partway through, it automatically becomes eligible for retry on the next poll instead of blocking forever.',
      'Added a “Clear stuck/failed claim” button to the Automated roster feed panel in Admin > Roster, so a stuck or failed import can be released for reprocessing immediately, without waiting.',
    ],
  },
  {
    version: '1.3.14',
    date: '2026-08-03',
    highlights: [
      'The automated roster feed no longer needs write access to the shared mailbox. It used to mark each imported NMMExport email as “read” after processing it, which required a broader mail permission than we wanted to grant. It now only reads mail — duplicate imports are still fully prevented the same way they always were (by remembering each email’s unique ID), so nothing changes about what gets imported or how often.',
    ],
  },
  {
    version: '1.3.13',
    date: '2026-08-03',
    highlights: [
      'Added a new "Intake" role and an Intake Conflict Check step that now runs first on every new matter, before any other approval — Intake must clear it before the matter can move on to Practice Group Leader/General Counsel/etc. review.',
      'Once a matter clears every required approval, Intake is now automatically emailed that it’s ready for client/matter numbering, with the same complete-record PDF the submitter receives.',
      'Admins can now set a distribution list (Admin > Areas of Law) for billing, records, walls/IT, or anyone else who needs the complete file the moment client and matter numbers are assigned — they get a fresh copy of the full-record PDF with the final numbers on it, whether the numbers were assigned automatically or entered manually from Admin > Numbering.',
    ],
  },
  {
    version: '1.3.12',
    date: '2026-08-03',
    highlights: [
      'Once a matter is fully approved, the notification email to the submitter now includes a PDF of the complete matter record — form answers and full approval history — so it can be filed into iManage or wherever else it needs to live.',
      'Admins can now set additional email addresses (Admin > Areas of Law) to automatically receive that same completed-matter PDF alongside the submitter.',
    ],
  },
  {
    version: '1.3.11',
    date: '2026-08-03',
    highlights: [
      'Admin > Roster now hides inactive people by default (with a “Show inactive” checkbox to bring them back into view or reactivate them) — useful for keeping junk entries that sometimes come through the automated roster feed out of the way without deleting anything.',
      'Fixed clicking “Edit” on someone in Admin > Roster: the edit panel now scrolls into view automatically instead of leaving you to scroll all the way back to the top of the page to find it.',
    ],
  },
  {
    version: '1.3.10',
    date: '2026-08-03',
    highlights: [
      'Fixed drafts silently disappearing from “My Matters”/“All matters” for approvers: a saved draft never recorded when it was created, and the approver list sorts by that field, so Firestore quietly left those matters out of the list even though the form correctly showed “Draft saved”. Every draft now gets its created-on time recorded the first time it’s saved, and any older draft missing it is repaired automatically the next time it’s opened and saved.',
    ],
  },
  {
    version: '1.3.9',
    date: '2026-08-03',
    highlights: [
      'Fixed the Practice Group Leader field not auto-filling when you pick a practice group — it now fills in that group’s leader automatically, and still lets you type over it by hand if needed.',
      'Fixed the Managing Partner initials box staying on screen after you switched the engagement letter from “waived” back to “signed on file” — toggling it back now clears the waiver questions and their answers.',
      'Matter Type is now a single choice — Litigation or Non-Litigation — instead of two separate checkboxes that could both be checked (or both left unchecked) at once.',
      'Redesigned Wall Builder Designation: instead of typing free-text initials, you now check off every practice group that should have access and search the firm roster to add specific individuals by name. At least one group or person is required before you can submit.',
      'Origination percentages must now add up to exactly 100% before you can submit a matter (checked both in the form and on the server) — with a clearer message showing your current total. A single working attorney is defaulted to 100% automatically.',
      'Fixed Save as Draft so a bad or missing value in the form can no longer silently block the save; a config-loading problem is now shown on screen instead of leaving the page looking stuck.',
      'Required fields now show a live red outline as soon as you leave them blank — no need to hit Submit first — and each section in the left-hand navigation is bolded with a checkmark once every required field in it is filled in.',
    ],
  },
  {
    version: '1.3.8',
    date: '2026-08-03',
    highlights: [
      'Added an automated daily import of the Responsible Partners / Practice Group Leads roster from the SSRS "NMMExport" email — the app now checks the mailbox each morning at 6am for that day\'s report (sent automatically around 3am) and imports it, so no one has to manually download and upload the CSV. The manual upload in Admin > Roster still works any time as a backup.',
      'Admin > Roster now shows the automated roster feed\'s last run — which email it processed and whether the import succeeded — plus a "Run poll now" button to import today\'s report immediately instead of waiting for the 6am schedule.',
    ],
  },
  {
    version: '1.3.7',
    date: '2026-07-31',
    highlights: [
      'Fixed Areas of Law getting stuck on "Loading…" forever in Admin whenever settings/general did not exist yet in production — the page was waiting on File subtypes to load from that document, but the code only initialized File subtypes when the document was present, so a missing document meant the page never finished loading. It now initializes correctly whether the document exists or not.',
      'The one-time settings seeder now also creates settings/general (firm name, app name, support contact, and File subtypes) if it is missing, and repairs File subtypes on an existing settings/general document if that field is not already a list — without touching any other fields on that document.',
      'Fixed "Send test email" in Admin > Mail Queue sending two copies of the same test email. Queuing the test message and then delivering it inline both tried to send it — the queue write alone was enough to trigger a second, independent delivery. Test emails now send exactly once.',
    ],
  },
  {
    version: '1.3.6',
    date: '2026-07-31',
    highlights: [
      'Fixed the Areas of Law admin page crashing on load, and fixed the same underlying bug on the live intake form — the "Areas of law" and "File subtypes" checkboxes were silently rendering as empty lists for everyone submitting a matter, not just failing in Admin.',
      'ABA task codes now has a text box to enter the actual codes (e.g. "L110, L120, A103") once you say yes to using ABA codes, instead of just a yes/no toggle.',
      'Attachments are now split into three categories — conflicts check documents, engagement letter, and other documents — each timestamped per upload so nothing gets overwritten, and approvers can now see all uploaded files (grouped by category) right on the approval page. Previously approvers had no visibility into attachments at all.',
      'New admin "Field builder" page: show/hide any field, reorder fields within a section, edit labels/help text/requirement level, and add brand-new custom fields (text, paragraph, yes/no, date, dropdown, checkbox list, number, email, phone, or money) — all without a deploy.',
      'Added a "Never copy" flag per field in the field builder. A field marked this way is left out of both "Clone this matter" and saved templates — one flag controls both.',
      '"Clone this matter" button on the matter detail page opens a new draft pre-filled from the existing matter (skipping never-copy fields, client/matter numbers, approval history, and attachments).',
      'Saved templates: save any in-progress matter as a reusable template (respecting the same never-copy flag), and start a new matter from a saved template via the "Start from template" option next to "New matter". Templates are visible to the whole firm; only the person who saved one (or an admin) can delete it.',
    ],
  },
  {
    version: '1.3.5',
    date: '2026-07-31',
    highlights: [
      'CSV import now matches a practice group even when the CSV value is a shortened version of the full name — e.g. "Commercial and Complex Litigation" now matches an existing "Commercial and Complex Litigation Practice Group". The import preview also now flags rows whose practice group doesn’t match anything before you import, so you can fix names or create missing groups first.',
    ],
  },
  {
    version: '1.3.4',
    date: '2026-07-31',
    highlights: [
      'CSV import of responsible partners now converts "Last, First" style names (the Aderant/SSRS export format, e.g. "WILLIAMS, TRICIA, L.") into "First Last" automatically, and title-cases all-caps names. Also recognizes EMPLOYEE_NAME and PracticeGroupDescription columns directly from that export format.',
    ],
  },
  {
    version: '1.3.3',
    date: '2026-07-31',
    highlights: [
      'Fixed the "Save" button in Firm Leadership doing nothing, and the Approval Flow editor getting stuck on "Loading flow..." forever — both happened when the underlying settings document had never been created (only the local emulator seed script created it automatically; production never did). Saving now creates the missing settings with sane defaults automatically instead of silently failing.',
    ],
  },
  {
    version: '1.3.2',
    date: '2026-07-31',
    highlights: [
      'Added a "No admin yet? Claim access" button in the header, shown to any signed-in user with zero roles. It only works while the roster is completely empty, so the very first user can reach Admin without any manual Firestore editing.',
    ],
  },
  {
    version: '1.3.1',
    date: '2026-07-31',
    highlights: [
      'Fixed a first-run gap: if nobody had been granted admin yet, there was no way to become the first admin/superadmin at all. Added a one-time bootstrap that works only while the roster is empty.',
    ],
  },
  {
    version: '1.3.0',
    date: '2026-07-31',
    highlights: [
      'Added a superadmin role tier — only a superadmin can grant or revoke Administrator/Superadmin access; other admins can still manage every other role.',
      'Admin > Roster now has a Firm Leadership panel to set the Managing Partner (name + email) used by approval-flow fallback steps.',
      'Practice Groups admin now shows each leader’s email inline and supports creating new practice groups and renaming existing ones.',
      'Added a manual CSV import for Responsible Partners (Admin > Roster) — upload a Name/Email/Practice Group export and it upserts the roster and links practice groups automatically.',
      'Added a version pill in the header with a “What’s new” changelog popover.',
      'Approval Flow admin now has a structured condition builder — pick Practice Group or Area of Law from dropdowns instead of hand-editing JSON — to route steps (and their approve/reject outcome) based on the matter’s practice group or area of law.',
    ],
  },
  {
    version: '1.2.0',
    date: '2026-07-31',
    highlights: [
      'Added a "Send test email" tool to Admin > Mail Queue so admins can verify Microsoft Graph mail delivery without waiting for a real approval notification.',
    ],
  },
  {
    version: '1.1.0',
    date: '2026-07-31',
    highlights: [
      'Wired up Microsoft Graph mail sending and the magic-link signing secret as production Firebase secrets.',
      'Enabled Entra ID SAML SSO sign-in.',
      'Security fix: prevented a misconfigured secret from leaking into the client bundle.',
    ],
  },
  {
    version: '1.0.0',
    date: '2026-07-30',
    highlights: [
      'Initial release: new matter intake form, configurable approval workflow engine, approver magic links, admin console (roster, approval flow, field rules, areas of law, numbering).',
    ],
  },
];

export const CURRENT_VERSION = CHANGELOG[0].version;
